import {Badge, Card, Form} from "react-bootstrap";
import {useNavigate, useParams} from "react-router-dom";
import {AddToCart} from "../../../../cart/modules/pages/cart/addToCart";
import {ProductModel} from "../../../domain/models/productModel";
import {FavoriteBtn} from "../../../../productFavorites/modules/commons/favoriteBtn";
import React, {useState} from "react";
import default307 from "../../commons/images/product/default-307.png";
import {formatNum} from "../../../../general/features/util";
import {useAppSelector}from "../../../../../hooks/useTypedSelector";
import {useTranslation} from "react-i18next";
import {ProductQuoteDataInfoModal} from "../../features/product/ProductQuoteDataInfoModal";

export const onProdImgError = (e: React.SyntheticEvent) => {
    (e.target as HTMLImageElement).src = default307;
}

export function ProductCard(props: { product: ProductModel, onPriceRequest: (productId: string, name: string, quantity: number, minQuantity?: number) => void }) {
    const {product, onPriceRequest} = props;
    const {lang} = useParams();
    const {t} = useTranslation('common');

    const navigation = useNavigate();
    const thumbnail = product.images.length > 0 ? product.images[0].thumbnail : default307;

    const {currentUser} = useAppSelector((state) => state.currentUser);
    const {upcoming_price_mutations} = useAppSelector((state) => state.productRange);

    const gotoProduct = (event: {preventDefault: () => void}) => {
        event.preventDefault();

        navigation(`/${lang}/assortment/${product.id}`)
    };

    const [selectedQuantity, setSelectedQuantity] = useState(1);
    const variationOptions = product.variation_options ?? [];
    const [selectedVariationId, setSelectedVariationId] = useState(
        product.variations?.drupal_internal__variation_id ?? 0
    );
    const availableVariationOptions = variationOptions.filter(option => {
        if (option.disabled) {
            return false;
        }
        return option.max_quantity === undefined || option.max_quantity > 0;
    });
    const selectedOption = availableVariationOptions.find(option => option.drupal_internal__variation_id === selectedVariationId)
        ?? availableVariationOptions.find(option => option.drupal_internal__variation_id === product.variations?.drupal_internal__variation_id)
        ?? availableVariationOptions[0]
        ?? variationOptions.find(option => !option.disabled)
        ?? variationOptions[0];
    const regularOption = availableVariationOptions.find(option => !option.is_limited_stock);
    const actionOption = availableVariationOptions.find(option => option.is_limited_stock);
    const showActionSwitch = !!regularOption && !!actionOption;
    const selectedScaledPrices = selectedOption?.scaled_prices ?? product.scaled_prices ?? [];
    const hasSelectedThtOption = !!(selectedOption?.is_limited_stock && selectedOption.max_quantity !== undefined && selectedOption.max_quantity > 0);
    const showActionBadge = !!(product.action_active && product.action_label && (product.action_type !== 'tht' || hasSelectedThtOption));

    const addQuantity = (quantity: number) => {
        setSelectedQuantity(quantity);
    };

    const isWholesale = ['GROOTH', 'GH-INT'].includes(`${currentUser?.data.active_relation?.pricelist}`)

    const disabled = ((selectedScaledPrices.length ?? 0) === 0 && isWholesale) || selectedOption?.disabled;

    const minQuantity = selectedScaledPrices.length > 0 ? Math.min(...selectedScaledPrices.map(price => price.quantity ?? 1)) : 1;

    const handlePriceReq = () => {
      onPriceRequest(product.drupal_internal__product_id, product.name, selectedQuantity, minQuantity);
    }

    const upcomingPrice = Object.values(upcoming_price_mutations).find(m => parseInt(m.product) === parseInt(`${product.cb_product_id}`));

    return (
        <Card className={'product-card'}>
                <div className={'product-card-image'}>
                    <a onClick={gotoProduct} href={`/${lang}/assortment/${product.id}`}>
                        <Card.Img
                            variant={'top'}
                            src={thumbnail}
                            onError={onProdImgError}
                            draggable={false}
                            loading={'lazy'}
                            decoding={'async'}
                            style={{
                                display: 'block',
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </a>

                    <div className={'product-card-badges'}>
                        {showActionBadge && (
                            <Badge className={'product-action-badge-primary'}>{product.action_label}</Badge>
                        )}
                        {selectedOption?.is_limited_stock && selectedOption.max_quantity !== undefined && (
                            <Badge className={'product-action-badge-secondary'}>
                                {t('Limited stock')}: {selectedOption.max_quantity} {t('available')}
                            </Badge>
                        )}
                    </div>

                    <div style={{display: 'block', position: 'absolute', right: 5, top: 5}}>
                        <ProductQuoteDataInfoModal product={product} />
                    </div>
                </div>
            <Card.Body className={"d-flex flex-column justify-content-between"}>
                <a onClick={gotoProduct} href={`/${lang}/assortment/${product.id}`}>
                    <Card.Title>{product.name ?? ''}</Card.Title>
                    <Card.Text className={'article-number'}>
                        Art. nr.: {selectedOption?.sku ?? product.sku ?? ''}
                    </Card.Text>
                </a>
                <div>
                    <div className={'upcoming-price'}>
                        {upcomingPrice && <p>
                            {t('@percentage% @op from @date').replace('@date', upcomingPrice.mutation.date).replace('@percentage', upcomingPrice.mutation.percentage).replace('@op', upcomingPrice.mutation.oldPrice < upcomingPrice.mutation.newPrice ? t('more expensive') : t('cheaper'))}
                        </p>}
                    </div>
                    <div className={'product-price-badge'}>
                        {selectedScaledPrices.length > 0 ? selectedScaledPrices.map((item, key) =>
                            <Badge key={key} bg={'primary'} onClick={() => addQuantity(item.quantity > 0 ? item.quantity : 1)}>
                                {formatNum(`${item.price ?? ''}`)}
                                {item.quantity > 0 && <small>{item.quantity} st.</small>}
                            </Badge>
                        ) : (
                          isWholesale ? (
                            <Badge bg={'primary'} className={'cursor-pointer'} onClick={handlePriceReq}>{t('Price on request')}</Badge>
                            ) : (
                            <Badge bg={'primary'}>{product.price ?? ''}</Badge>
                          )
                        )}
                    </div>
                    <div className={'add-to-cart justify-content-between d-flex'}>
                        <div className={'d-flex flex-row flex-grow'}>
                            <div className={'d-flex'}>
                                <AddToCart variationId={selectedOption?.drupal_internal__variation_id ?? product.variations.drupal_internal__variation_id}
                                           variant={'card'}
                                           quantity={selectedQuantity}
                                           btnDisabled={disabled}
                                           minQuantity={minQuantity}
                                           maxQuantity={selectedOption?.max_quantity}
                                           onPriceRequest={() => handlePriceReq()}
                                           updateQuantity={(q) => setSelectedQuantity(q)}
                                />
                            </div>
                        </div>
                        <div>
                            <FavoriteBtn size={'25px'} productId={product.id} variant={'card'}/>
                        </div>
                    </div>
                    {showActionSwitch && (
                        <div className={'action-variation-switch mt-2'}>
                            <Form.Check
                                type={'switch'}
                                id={`action-variation-${product.id}`}
                                label={t('Use THT action')}
                                checked={selectedOption?.id === actionOption.id}
                                onChange={(event) => {
                                    setSelectedVariationId(
                                        event.currentTarget.checked
                                            ? actionOption.drupal_internal__variation_id
                                            : regularOption.drupal_internal__variation_id
                                    );
                                    setSelectedQuantity(1);
                                }}
                            />
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
}
